// frontend/src/components/Navigation.js
// --- MODIFIED FILE ---
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// MODIFICATION: Import de ListChecks
import { Users, FolderKanban, Clock, Building2, Clipboard, LogOut, BarChart3, Receipt, Settings, ChevronDown, ListChecks, Menu, X, Circle, Package, Gauge, Wrench, Home, Mic, MessageSquareText, Cpu, ShieldCheck } from "lucide-react";
import { useAuth } from './Auth';
import InnowideLogo from './InnowideLogo';
import NotificationBell from './Notifications/NotificationBell';
import { MeetingRecordButton } from './Meetings/MeetingRecordButton';
import { apiService } from '../services/api';
import useHomeNews from './Home/homeNews';

const Navigation = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMeetingCount, setOpenMeetingCount] = useState(0);
  const [pendingVotesCount, setPendingVotesCount] = useState(0);
  const [expenseAttention, setExpenseAttention] = useState(0); // factures déposées qui attendent la personne
  const location = useLocation();
  const API_URL = apiService.getBaseUrl();
  const isSuperAdmin = Number(user?.employee_id ?? user?.id) === 1;
  const isAdmin = Boolean(user?.is_admin) || isSuperAdmin;
  const canViewPilotage = isAdmin || Boolean(user?.can_view_pilotage);
  // Nouveautés sur l'accueil depuis la dernière visite : légère teinte et pastille sur « Accueil ».
  const homeNews = useHomeNews(Boolean(user?.can_use_home), location.pathname === '/');

  const optionsRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) {
        setShowOptionsMenu(false);
      }
      if (userRef.current && !userRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const fetchAttention = async () => {
      if (!user?.employee_id) return;
      try {
        const data = await apiService.get('/expense-drafts/attention');
        if (active) setExpenseAttention(Number(data?.count) || 0);
      } catch (err) {
        if (active) setExpenseAttention(0);
      }
    };
    fetchAttention();
    const interval = setInterval(fetchAttention, 60000);
    return () => { active = false; clearInterval(interval); };
  }, [user, location.pathname]);

  const expenseBadge = expenseAttention > 0 && (
    <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold leading-5 text-white"
      title={`${expenseAttention} facture${expenseAttention > 1 ? 's' : ''} à voir`}>{expenseAttention}</span>
  );

  useEffect(() => {
    let active = true;
    const fetchOpenMeetings = async () => {
      if (!user?.id) {
        if (active) setOpenMeetingCount(0);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/circle-meetings/open-for-user`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Open meetings fetch failed');
        const data = await response.json();
        if (active) setOpenMeetingCount(Array.isArray(data) ? data.length : 0);
      } catch (err) {
        if (active) setOpenMeetingCount(0);
      }
    };
    fetchOpenMeetings();
    const interval = setInterval(fetchOpenMeetings, 10000);
    const handleFocus = () => fetchOpenMeetings();
    window.addEventListener('focus', handleFocus);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [API_URL, getAuthHeaders, user]);

  useEffect(() => {
    let active = true;
    const fetchPendingVotes = async () => {
      if (!user?.id) {
        if (active) setPendingVotesCount(0);
        return;
      }
      try {
        const response = await fetch(`${API_URL}/circle-votes/pending`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Pending votes fetch failed');
        const data = await response.json();
        if (active) setPendingVotesCount(Array.isArray(data) ? data.length : 0);
      } catch (err) {
        if (active) setPendingVotesCount(0);
      }
    };
    fetchPendingVotes();
    const interval = setInterval(fetchPendingVotes, 15000);
    const handleFocus = () => fetchPendingVotes();
    window.addEventListener('focus', handleFocus);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [API_URL, getAuthHeaders, user]);

  return (
    <nav className="bg-white shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between gap-3 h-16 items-center">
          <div className="flex shrink-0 items-center gap-3">
            {/* Mobile menu button */}
            <button
              className="relative xl:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-200 focus:outline-none"
              aria-label="Ouvrir le menu"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              {homeNews && !mobileOpen && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full border-2 border-white bg-indigo-500" />}
            </button>
            <div className="flex-shrink-0">
              <InnowideLogo />
            </div>
          </div>

          <div className="hidden xl:flex flex-nowrap items-center gap-1">
            {user?.can_use_home && (
              <button
                onClick={() => navigate('/')}
                title={homeNews ? 'Du nouveau sur ton accueil' : undefined}
                className={`relative flex items-center whitespace-nowrap px-2.5 py-2 rounded-md text-sm font-medium hover:bg-gray-200
                  ${location.pathname === '/' ? 'bg-gray-200 text-blue-600' : homeNews ? 'bg-indigo-50 text-indigo-700' : 'text-gray-900'}`}
              >
                <Home className="h-4 w-4 mr-2" />
                Accueil
                {homeNews && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-indigo-500" />}
              </button>
            )}
            <button
              onClick={() => navigate('/projects')}
              className={`flex items-center whitespace-nowrap px-2.5 py-2 rounded-md text-sm font-medium hover:bg-gray-200
                ${location.pathname.startsWith('/projects') ? 'bg-gray-200 text-blue-600' : 'text-gray-900'}`}
            >
              <FolderKanban className="h-4 w-4 mr-2" />
              Projets
            </button>
            <button
              onClick={() => navigate('/objectives')}
              className={`flex items-center whitespace-nowrap px-2.5 py-2 rounded-md text-sm font-medium hover:bg-gray-200
                ${location.pathname.startsWith('/objectives') ? 'bg-gray-200 text-blue-600' : 'text-gray-900'}`}
            >
              <Clipboard className="h-4 w-4 mr-2" />
              Objectifs
            </button>
            {/* --- MODIFICATIONS CI-DESSOUS --- */}
            <button
              onClick={() => navigate('/backlog')}
              className={`flex items-center whitespace-nowrap px-2.5 py-2 rounded-md text-sm font-medium hover:bg-gray-200
                ${location.pathname.startsWith('/backlog') ? 'bg-gray-200 text-blue-600' : 'text-gray-900'}`}
            >
              <ListChecks className="h-4 w-4 mr-2" />
              Tâches
            </button>
            {/* --- FIN DES MODIFICATIONS --- */}
            <button
              onClick={() => navigate('/holacracy')}
              className={`relative flex items-center whitespace-nowrap px-2.5 py-2 rounded-md text-sm font-medium hover:bg-gray-200
                ${location.pathname.startsWith('/holacracy') ? 'bg-gray-200 text-blue-600' : 'text-gray-900'}`}
            >
              <Circle className="h-4 w-4 mr-2" />
              Organisation
              {pendingVotesCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-semibold text-white">
                  {pendingVotesCount > 9 ? '9+' : pendingVotesCount}
                </span>
              )}
              {openMeetingCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-500" />
              )}
            </button>
            <button
              onClick={() => navigate('/hours')}
              className={`flex items-center whitespace-nowrap px-2.5 py-2 rounded-md text-sm font-medium hover:bg-gray-200
                ${location.pathname.startsWith('/hours') ? 'bg-gray-200 text-blue-600' : 'text-gray-900'}`}
            >
              <Clock className="h-4 w-4 mr-2" />
              Mes heures
            </button>
            <button
              onClick={() => navigate('/expenses')}
              className={`flex items-center whitespace-nowrap px-2.5 py-2 rounded-md text-sm font-medium hover:bg-gray-200
                ${location.pathname.startsWith('/expenses') ? 'bg-gray-200 text-blue-600' : 'text-gray-900'}`}
            >
              <Receipt className="h-4 w-4 mr-2" />
              Dépenses{expenseBadge}
            </button>
            <div className="relative" ref={optionsRef}>
              <button
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                className="flex items-center whitespace-nowrap px-2.5 py-2 rounded-md text-sm font-medium hover:bg-gray-200 text-gray-900"
              >
                <Settings className="h-4 w-4 mr-2" />
                Autres
                <ChevronDown className="h-4 w-4 ml-1" />
              </button>
              {showOptionsMenu && (
                <div className="absolute mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    {isAdmin && (
                      <button
                        onClick={() => { navigate('/dashboard'); setShowOptionsMenu(false); }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Dashboard
                      </button>
                    )}
                    {canViewPilotage && (
                      <button
                        onClick={() => { navigate('/pilotage'); setShowOptionsMenu(false); }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Gauge className="h-4 w-4 mr-2" />
                        Pilotage
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => { navigate('/ai-usage'); setShowOptionsMenu(false); }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <Cpu className="h-4 w-4 mr-2" />
                        Consommation IA
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => { navigate('/nexus-rights'); setShowOptionsMenu(false); }}
                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <ShieldCheck className="h-4 w-4 mr-2" />
                        Droits de Nexus
                      </button>
                    )}
                    <button
                      onClick={() => { navigate('/meetings'); setShowOptionsMenu(false); }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Mic className="h-4 w-4 mr-2" />
                      Mes réunions
                    </button>
                    <button
                      onClick={() => { navigate('/points'); setShowOptionsMenu(false); }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <MessageSquareText className="h-4 w-4 mr-2" />
                      Mes points à aborder
                    </button>
                    <button
                      onClick={() => { navigate('/revue-avancement'); setShowOptionsMenu(false); }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Gauge className="h-4 w-4 mr-2" />
                      Revue d'avancement
                    </button>
                    <button
                      onClick={() => { navigate('/tools'); setShowOptionsMenu(false); }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Wrench className="h-4 w-4 mr-2" />
                      Outils
                    </button>
                    <button
                      onClick={() => { navigate('/billing-batches'); setShowOptionsMenu(false); }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Receipt className="h-4 w-4 mr-2" />
                      Commandes
                    </button>
                    {isAdmin && (
                    <button
                      onClick={() => { navigate('/employees'); setShowOptionsMenu(false); }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Employés
                    </button>
                    )}
                    <button
                      onClick={() => { navigate('/clients'); setShowOptionsMenu(false); }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Building2 className="h-4 w-4 mr-2" />
                      Clients
                    </button>
                    <button
                      onClick={() => { navigate('/inventory'); setShowOptionsMenu(false); }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Package className="h-4 w-4 mr-2" />
                      PLM
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* User Profile Menu */}
          <div className="flex items-center gap-4">
            <MeetingRecordButton />
            <div className="relative" ref={userRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-3 focus:outline-none"
              >
                {user?.picture && !avatarBroken ? (
                  <img
                    src={user.picture}
                    alt=""
                    referrerPolicy="no-referrer"
                    onError={() => setAvatarBroken(true)}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-700">
                    {(user?.name || '?').charAt(0).toUpperCase()}
                  </span>
                )}
              </button>
              {/* Dropdown menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b">
                      <div className="font-medium">{user?.name}</div>
                      <div className="text-gray-500 text-xs truncate">
                        {user?.email}
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/ai-preferences');
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Mes réglages IA
                    </button>
                    <button onClick={() => { setShowUserMenu(false); navigate('/agent-access'); }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <Settings className="h-4 w-4 mr-2" />Accès agents &amp; API
                    </button>
                    <button
                      onClick={logout}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Se déconnecter
                    </button>
                  </div>
                </div>
              )}
            </div>
            <NotificationBell />
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      <div className={`${mobileOpen ? 'block' : 'hidden'} xl:hidden border-t border-gray-200 bg-white`}
        role="menu"
        aria-label="Navigation mobile">
        <div className="px-4 py-3 space-y-2">
          {isAdmin && (
            <button
              onClick={() => { navigate('/dashboard'); setMobileOpen(false); }}
              className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/dashboard') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Dashboard
            </button>
          )}
          {canViewPilotage && (
            <button
              onClick={() => { navigate('/pilotage'); setMobileOpen(false); }}
              className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/pilotage') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
            >
              <Gauge className="h-4 w-4 mr-2" />
              Pilotage
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => { navigate('/ai-usage'); setMobileOpen(false); }}
              className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/ai-usage') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
            >
              <Cpu className="h-4 w-4 mr-2" />
              Consommation IA
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => { navigate('/nexus-rights'); setMobileOpen(false); }}
              className="flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 text-gray-900"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Droits de Nexus
            </button>
          )}
          {user?.can_use_home && (
            <button
              onClick={() => { navigate('/'); setMobileOpen(false); }}
              className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname === '/' ? 'bg-gray-100 text-blue-600' : homeNews ? 'bg-indigo-50 text-indigo-700' : 'text-gray-900'}`}
            >
              <Home className="h-4 w-4 mr-2" />
              Accueil
              {homeNews && <span className="ml-2 h-1.5 w-1.5 rounded-full bg-indigo-500" />}
            </button>
          )}
          <button
            onClick={() => { navigate('/projects'); setMobileOpen(false); }}
            className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/projects') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
          >
            <FolderKanban className="h-4 w-4 mr-2" />
            Projets
          </button>
          <button
            onClick={() => { navigate('/objectives'); setMobileOpen(false); }}
            className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/objectives') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
          >
            <Clipboard className="h-4 w-4 mr-2" />
            Objectifs
          </button>
          <button
            onClick={() => { navigate('/backlog'); setMobileOpen(false); }}
            className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/backlog') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
          >
            <ListChecks className="h-4 w-4 mr-2" />
            Tâches
          </button>
          <button
            onClick={() => { navigate('/hours'); setMobileOpen(false); }}
            className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/hours') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
          >
            <Clock className="h-4 w-4 mr-2" />
            Mes heures
          </button>
          <button
            onClick={() => { navigate('/expenses'); setMobileOpen(false); }}
            className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/expenses') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
          >
            <Receipt className="h-4 w-4 mr-2" />
            Dépenses{expenseBadge}
          </button>
          <div className="border-t border-gray-200 pt-2">
            <button
              onClick={() => { navigate('/tools'); setMobileOpen(false); }}
              className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/tools') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
            >
              <Wrench className="h-4 w-4 mr-2" />
              Outils
            </button>
            <button
              onClick={() => { navigate('/billing-batches'); setMobileOpen(false); }}
              className={`flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 ${location.pathname.startsWith('/billing-batches') ? 'bg-gray-100 text-blue-600' : 'text-gray-900'}`}
            >
              <Receipt className="h-4 w-4 mr-2" />
              Commandes
            </button>
            {isAdmin && (
            <button
              onClick={() => { navigate('/employees'); setMobileOpen(false); }}
              className="flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 text-gray-900"
            >
              <Users className="h-4 w-4 mr-2" />
              Employés
            </button>
            )}
            <button
              onClick={() => { navigate('/clients'); setMobileOpen(false); }}
              className="flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 text-gray-900"
            >
              <Building2 className="h-4 w-4 mr-2" />
              Clients
            </button>
            <button
              onClick={() => { navigate('/inventory'); setMobileOpen(false); }}
              className="flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 text-gray-900"
            >
              <Package className="h-4 w-4 mr-2" />
              PLM
            </button>
            <button
              onClick={() => { navigate('/ai-preferences'); setMobileOpen(false); }}
              className="flex w-full items-center px-3 py-2 mt-2 rounded-md text-sm font-medium hover:bg-gray-100 text-gray-900"
            >
              <Settings className="h-4 w-4 mr-2" />
              Mes réglages IA
            </button>
            <button onClick={() => { navigate('/agent-access'); setMobileOpen(false); }}
              className="flex w-full items-center px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 text-gray-900">
              <Settings className="h-4 w-4 mr-2" />Accès agents &amp; API
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
